import React, { useState } from 'react';
import { X, MessageCircle, Send, AlertCircle } from 'lucide-react';
import { useTheme } from '../arrangement/ThemeContext';
const CreateDiscussionDialog = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  currentUser = null,
  isAuthenticated = false,
  onLogin = () => {}
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const { colors } = useTheme(); 

  const validateForm = () => {
    const newErrors = {};
    
    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.length > 200) {
      newErrors.title = 'Title must be 200 characters or less';
    }
    
    if (!content.trim()) {
      newErrors.content = 'Content is required';
    } else if (content.length < 10) {
      newErrors.content = 'Discussion must be at least 10 characters long';
    } else if (content.length > 1000) {
      newErrors.content = 'Content must be 1000 characters or less';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      onLogin();
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setErrors({});
      
      await onSubmit({
        title: title.trim(),
        content: content.trim()
      });
      
      // Reset form and close
      setTitle('');
      setContent('');
      onClose();
      
    } catch (error) {
      setErrors({ general: error.message || 'Failed to create discussion' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setContent('');
    setErrors({});
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  const clearFieldError = (field) => {
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px',
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
        borderRadius: '20px',
        padding: '24px',
        border: '1px solid #333',
        width: '100%',
        maxWidth: '500px',
        position: 'relative',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <MessageCircle size={20} style={{ color: 'white' }} />
            </div>
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '700',
              color: '#e5e5e5'
            }}>
              Create Discussion
            </h2>
          </div>
          
          <button
            onClick={handleClose}
            style={{
              padding: '8px',
              background: 'transparent',
              border: '1px solid #333',
              borderRadius: '8px',
              color: '#888',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#ef4444';
              e.currentTarget.style.color = '#ef4444';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#333';
              e.currentTarget.style.color = '#888';
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* User Info */}
        {isAuthenticated && currentUser && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px',
            background: colors.bgCard,
            borderRadius: '12px',
            border: '1px solid #333',
            marginBottom: '20px'
          }}>
            <img
              src={currentUser.avatar || `https://ui-avatars.com/api/?name=${currentUser.username}&background=00d4aa&color=fff`}
              alt={currentUser.username}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                objectFit: 'cover'
              }}
            />
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#e5e5e5' }}>
                {currentUser.username}
              </div>
              <div style={{ fontSize: '12px', color: '#888' }}>
                {currentUser.smoker_level || 'Member'}
              </div>
            </div>
          </div>
        )}

        {/* General Error */}
        {errors.general && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            background: '#ef444420',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <AlertCircle size={16} style={{ color: '#ef4444' }} />
            <span style={{ color: '#ef4444', fontSize: '14px' }}>
              {errors.general}
            </span>
          </div>
        )}

        {/* Title Field */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#e5e5e5',
            marginBottom: '8px'
          }}>
            Discussion Title *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              clearFieldError('title');
            }}
            onKeyDown={handleKeyPress}
            placeholder="What would you like to discuss about this research?"
            style={{
              width: '100%',
              padding: '12px 16px',
              background: colors.bgCard,
              border: errors.title ? '1px solid #ef4444' : '1px solid #333',
              borderRadius: '12px',
              color: '#e5e5e5',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'all 0.3s ease'
            }}
            onFocus={(e) => {
              if (!errors.title) {
                e.currentTarget.style.borderColor = '#00d4aa';
              }
            }}
            onBlur={(e) => {
              if (!errors.title) {
                e.currentTarget.style.borderColor = '#333';
              }
            }}
          />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '4px',
            fontSize: '12px'
          }}>
            <div style={{ color: errors.title ? '#ef4444' : 'transparent' }}>
              {errors.title || ' '}
            </div>
            <div style={{ color: title.length > 180 ? '#ef4444' : '#666' }}>
              {title.length}/200
            </div>
          </div>
        </div>

        {/* Content Field */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#e5e5e5',
            marginBottom: '8px'
          }}>
            Discussion Content *
          </label>
          <textarea
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              clearFieldError('content');
            }}
            onKeyDown={handleKeyPress}
            placeholder="Share your thoughts, experiences, questions, or insights about this research..."
            style={{
              width: '100%',
              minHeight: '120px',
              padding: '16px',
              background: colors.bgCard,
              border: errors.content ? '1px solid #ef4444' : '1px solid #333',
              borderRadius: '12px',
              color: '#e5e5e5',
              fontSize: '14px',
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box',
              lineHeight: '1.5',
              transition: 'all 0.3s ease'
            }}
            onFocus={(e) => {
              if (!errors.content) {
                e.currentTarget.style.borderColor = '#00d4aa';
              }
            }}
            onBlur={(e) => {
              if (!errors.content) {
                e.currentTarget.style.borderColor = '#333';
              }
            }}
          />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '8px',
            fontSize: '12px'
          }}>
            <div style={{ color: errors.content ? '#ef4444' : 'transparent' }}>
              {errors.content || ' '}
            </div>
            <div style={{ color: content.length > 900 ? '#ef4444' : '#666' }}>
              {content.length}/1000
            </div>
          </div>
          <div style={{
            fontSize: '11px',
            color: '#666',
            marginTop: '4px'
          }}>
            💡 Tip: Press Ctrl+Enter to submit quickly
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={handleClose}
            style={{
              padding: '12px 20px',
              background: 'transparent',
              color: '#888',
              border: '1px solid #333',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#666';
              e.currentTarget.style.color = '#e5e5e5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#333';
              e.currentTarget.style.color = '#888';
            }}
          >
            Cancel
          </button>
          
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || !content.trim() || isSubmitting || !isAuthenticated}
            style={{
              padding: '12px 24px',
              background: title.trim() && content.trim() && !isSubmitting && isAuthenticated
                ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
                : '#333',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: title.trim() && content.trim() && !isSubmitting && isAuthenticated ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
          >
            {isSubmitting ? (
              <>
                <div style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid transparent',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Creating...
              </>
            ) : (
              <>
                <Send size={14} />
                Create Discussion
              </>
            )}
          </button>
        </div>

        {/* Login Prompt for Non-Authenticated Users */}
        {!isAuthenticated && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: '#8b5cf620',
            border: '1px solid #8b5cf6',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0 0 8px 0', color: '#8b5cf6', fontSize: '13px' }}>
              You need to be logged in to create discussions
            </p>
            <button
              onClick={onLogin}
              style={{
                padding: '6px 12px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              Login Now
            </button>
          </div>
        )}

        {/* CSS for animations */}
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default CreateDiscussionDialog;